import { AuthError } from '@supabase/supabase-js';
import { UserWithDisplaySettings } from '../../../common/types/databaseTypes';
import {
    supabase,
    User,
    LoginCredentials,
    SignUpCredentials,
    createBaseAuthService,
    BaseAuthServiceType,
} from '@tickitacka/shared';
import {
    DEFAULT_GOAL_DISPLAY_SETTINGS,
    DEFAULT_TODO_DISPLAY_SETTINGS,
} from '@/common/constants/defaultSettings';
import i18next from '@/translation/i18n';

// main-app 전용 인증 서비스 타입 - BaseAuthServiceType을 확장
interface MainAppAuthServiceType
    extends Omit<BaseAuthServiceType<User>, 'getCurrentUser' | 'login'> {
    // getCurrentUser와 login은 UserWithDisplaySettings를 반환하므로 재정의
    getCurrentUser(): Promise<UserWithDisplaySettings>;
    login(credentials: LoginCredentials): Promise<UserWithDisplaySettings>;

    // main-app 전용 추가 메서드
    updateUserData(
        userId: string,
        userData: Partial<User>
    ): Promise<UserWithDisplaySettings>;
    updatePassword(newPassword: string): Promise<void>;
    resendConfirmationEmail(email: string): Promise<void>;
}

// 공유된 기본 인증 서비스 생성
const baseAuthService = createBaseAuthService(i18next);

// main-app 전용 인증 서비스
export const authService: MainAppAuthServiceType = {
    // 1. 공통 기능 구현
    async getCurrentUser(): Promise<UserWithDisplaySettings> {
        try {
            const {
                data: { user },
                error,
            } = await supabase.auth.getUser();

            if (error instanceof AuthError) {
                console.log('로그인이 필요합니다');
                return null;
            }

            if (error) {
                console.error('인증 오류:', error);
                return null;
            }

            if (!user) return null;

            const { data: userData, error: userError } = await supabase
                .from('user')
                .select(
                    `
                        *,
                        todo_display_setting: user_todo_display_setting!user_id(
                            show_goals,
                            show_empty_goals
                        ),
                        goal_display_setting: user_goal_display_setting!user_id(
                            completed_filter,
                            display_properties,
                            grouping,
                            ordering,
                            ordering_dir,
                            view_type
                        )
                    `
                )
                .eq('id', user.id)
                .filter('todo_display_setting.enable', 'eq', true)
                .filter('goal_display_setting.enable', 'eq', true)
                .single<UserWithDisplaySettings>();

            if (userError) {
                console.error('사용자 정보 조회 오류:', userError);
                throw new Error('User data not found');
            }

            if (!userData) throw new Error('User data not found');

            // 사용자 언어 설정이 있으면 적용
            if (userData.language) {
                void i18next.changeLanguage(userData.language);
                localStorage.setItem('i18nextLng', userData.language);
            }

            // 기본 보기 설정 적용
            if (!userData.todo_display_setting) {
                userData.todo_display_setting = DEFAULT_TODO_DISPLAY_SETTINGS;
            }

            if (!userData.goal_display_setting) {
                userData.goal_display_setting = DEFAULT_GOAL_DISPLAY_SETTINGS;
            }

            return userData;
        } catch (error) {
            console.error('사용자 정보 조회 중 오류 발생:', error);
            return null;
        }
    },

    // 기본 인증 서비스에서 가져온 메서드들
    getUserById: (id: string) => baseAuthService.getUserById(id),
    getUserByEmail: (email: string) => baseAuthService.getUserByEmail(email),
    checkEmailExists: (email: string) =>
        baseAuthService.checkEmailExists(email),
    signUp: (credentials: SignUpCredentials) =>
        baseAuthService.signUp(credentials),
    logout: () => baseAuthService.logout(),

    // main-app 전용 오버라이드 메서드
    async login(
        credentials: LoginCredentials
    ): Promise<UserWithDisplaySettings> {
        await baseAuthService.login(credentials);
        return authService.getCurrentUser();
    },

    // 사용자 정보 업데이트 메소드
    async updateUserData(
        userId: string,
        userData: Partial<User>
    ): Promise<UserWithDisplaySettings> {
        try {
            const { data, error } = await supabase
                .from('user')
                .update({
                    ...userData,
                    update_date: new Date().toISOString(),
                })
                .eq('id', userId)
                .select(
                    `
                        *,
                        todo_display_setting:user_todo_display_setting(
                            show_goals,
                            show_empty_goals
                        ),
                        goal_display_setting:user_goal_display_setting(
                            completed_filter,
                            display_properties,
                            grouping,
                            ordering,
                            ordering_dir,
                            view_type
                        )
                    `
                )
                .single<UserWithDisplaySettings>();

            if (error) {
                console.error('사용자 정보 업데이트 오류:', error);
                throw new Error(
                    i18next.t('auth:sign_up_form.error.update_failed')
                );
            }

            if (!data) return null;

            // 기본 보기 설정 적용
            if (!data.todo_display_setting) {
                data.todo_display_setting = DEFAULT_TODO_DISPLAY_SETTINGS;
            }

            if (!data.goal_display_setting) {
                data.goal_display_setting = DEFAULT_GOAL_DISPLAY_SETTINGS;
            }

            return data;
        } catch (error) {
            console.error('사용자 정보 업데이트 중 오류 발생:', error);
            throw error;
        }
    },

    // 비밀번호 변경 메서드
    updatePassword: (newPassword: string) =>
        baseAuthService.updatePassword(newPassword),

    deleteAccount: (userId: string) => baseAuthService.deleteAccount(userId),

    resendConfirmationEmail: (email: string) =>
        baseAuthService.resendConfirmationEmail(email),
};
